/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLongPressAction } from "@/hooks/useLongPressAction";
import { Flag, Pencil, Reply, Send, Share2 } from "lucide-react";
import { XStack } from "tamagui";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MentionComposerField from "@/components/MentionComposerField";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import FullScreenImage from "@/components/FullScreenImage";
import KindredShareSheet from "@/components/KindredShareSheet";
import KindredSharedContent from "@/components/KindredSharedContent";
import MessageActions from "@/components/MessageActions";
import ReportSheet from "@/components/ReportSheet";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import {
  buildKindredWebUrl,
  composeKindredContent,
  parseKindredShareContent,
} from "@/lib/kindredShare";
import { dispatchMentionNotifications, searchChannelMentionSuggestions } from "@/lib/mentions";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import { toast } from "sonner";
import {
  getChannelCountryScopeLabel,
  normalizeChannelCountryScope,
  type MentionDraft,
} from "../../shared/community";
import {
  canEditOwnedContentOnly,
  canRemoveKinfeedOrChannelContent,
  isAdminModeratingOthersContent,
} from "../../shared/system";
import { AppButton, AppCard, AppCardBody, AppEmptyState, AppInput, AppLoadingState, AppText } from "../../ui-foundation";

const toTimestamp = (value?: string | null) => (value ? new Date(value).getTime() : 0);

const ChannelView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const longPressAction = useLongPressAction();
  const [channel, setChannel] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [reactions, setReactions] = useState<any[]>([]);
  const [replyCounts, setReplyCounts] = useState<Record<string, number>>({});
  const [newPost, setNewPost] = useState("");
  const [newPostMentions, setNewPostMentions] = useState<MentionDraft[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const [membershipBusy, setMembershipBusy] = useState(false);
  const [postActionsId, setPostActionsId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    if (!id) return;
    const [{ data: ch }, { data: ps }, { data: membership }] = await Promise.all([
      supabase.from("channels").select("*").eq("id", id).single(),
      supabase.from("posts").select("*, profiles(display_name, avatar_url)").eq("channel_id", id).order("created_at", { ascending: true }),
      user
        ? supabase.from("channel_members").select("channel_id, joined_at, last_read_at").eq("channel_id", id).eq("user_id", user.id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    if (ch) {
      setChannel({
        ...ch,
        is_joined: Boolean(membership),
        joined_at: membership?.joined_at ?? null,
      });
    }
    if (ps) {
      setPosts(ps);
      const postIds = ps.map((p: any) => p.id);
      if (postIds.length > 0) {
        const [{ data: rxns }, { data: reps }] = await Promise.all([
          supabase.from("post_reactions").select("*").in("post_id", postIds),
          supabase.from("replies").select("post_id, created_at, user_id").in("post_id", postIds),
        ]);
        if (rxns) setReactions(rxns);
        if (reps) {
          const counts: Record<string, number> = {};
          reps.forEach((r: any) => { counts[r.post_id] = (counts[r.post_id] || 0) + 1; });
          setReplyCounts(counts);
          if (membership && user) {
            const latestActivityAt = Math.max(
              ...ps.map((post: any) => toTimestamp(post.created_at)),
              ...reps.map((reply: any) => toTimestamp(reply.created_at)),
              0,
            );
            const readCutoff = Math.max(
              toTimestamp(membership.last_read_at),
              toTimestamp(membership.joined_at),
            );

            if (latestActivityAt > readCutoff) {
              const nextReadAt = new Date().toISOString();
              await supabase
                .from("channel_members")
                .update({ last_read_at: nextReadAt })
                .eq("channel_id", id)
                .eq("user_id", user.id);
              if (ch) {
                setChannel({
                  ...ch,
                  is_joined: Boolean(membership),
                  joined_at: membership?.joined_at ?? null,
                  last_read_at: nextReadAt,
                });
              }
            }
          }
        }
      }
    }
  };

  useEffect(() => { void fetchData(); }, [id, user?.id]);

  useEffect(() => {
    if (!id || !user?.id) return;
    const channel = supabase
      .channel(`channel-posts-${id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts", filter: `channel_id=eq.${id}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "post_reactions" }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "replies" }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "channel_members", filter: `user_id=eq.${user.id}` }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id, user?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [posts]);

  useEffect(() => {
    let active = true;
    const allPhotoValues = posts.flatMap((post: any) => post.photo_urls?.filter(Boolean) ?? []);

    void resolveSignedStorageUrls("channel-photos", allPhotoValues).then((nextMap) => {
      if (active) {
        setResolvedPhotoUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [posts]);

  const sendPost = async () => {
    if ((!newPost.trim() && !photoUrl) || !user || !id || sending) return;
    setSending(true);
    const { error } = await supabase.from("posts").insert({
      channel_id: id, user_id: user.id, content: newPost.trim() || "📷",
      photo_urls: photoUrl ? [photoUrl] : [],
    } as any);
    if (error) {
      toast.error(error.message);
      setSending(false);
      return;
    }
    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName:
        user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
      content: newPost.trim(),
      mentions: newPostMentions,
      bodyContext: `in ${channel?.name ?? "a channel"}`,
      entityType: "channel",
      entityId: id,
      url: `/community/channel/${id}`,
    });
    setNewPost("");
    setNewPostMentions([]);
    setPhotoUrl(null);
    setComposerOpen(false);
    setSending(false);
  };

  const toggleReaction = async (postId: string, emoji: string) => {
    if (!user) return;
    const existing = reactions.find((r) => r.post_id === postId && r.user_id === user.id && r.emoji === emoji);
    if (existing) {
      await supabase.from("post_reactions").delete().eq("id", existing.id);
    } else {
      await supabase.from("post_reactions").insert({ post_id: postId, user_id: user.id, emoji });
    }
    fetchData();
  };

  const deletePost = async (postId: string, removedByAdmin = false) => {
    await supabase.from("posts").delete().eq("id", postId);
    fetchData();
    toast.success(removedByAdmin ? "Post removed" : "Post deleted");
  };

  const saveEdit = async (postId: string) => {
    if (!editText.trim()) return;
    const existingPost = posts.find((item: any) => item.id === postId);
    const parsed = parseKindredShareContent(existingPost?.content);
    await supabase
      .from("posts")
      .update({
        content: composeKindredContent(editText, parsed.payload),
        edited_at: new Date().toISOString(),
      })
      .eq("id", postId);
    setEditingId(null);
    fetchData();
  };

  const getReactionsForPost = (postId: string) => {
    const postRxns = reactions.filter((r) => r.post_id === postId);
    const grouped: Record<string, { count: number; myReaction: boolean }> = {};
    postRxns.forEach((r) => {
      if (!grouped[r.emoji]) grouped[r.emoji] = { count: 0, myReaction: false };
      grouped[r.emoji].count++;
      if (r.user_id === user?.id) grouped[r.emoji].myReaction = true;
    });
    return grouped;
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffH = (now.getTime() - d.getTime()) / 3600000;
    if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`;
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const isJoined = Boolean(channel?.is_joined);

  const handleJoin = async () => {
    if (!user || !id || membershipBusy) return;

    setMembershipBusy(true);
    const { error } = await supabase.from("channel_members").insert({
      channel_id: id,
      user_id: user.id,
    });
    setMembershipBusy(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Channel joined");
    await fetchData();
  };

  const handleLeave = async () => {
    if (!user || !id || membershipBusy) return;

    if (!window.confirm(`Leave ${channel?.name ?? "this channel"}? You can still browse it later.`)) {
      return;
    }

    setMembershipBusy(true);
    const { error } = await supabase
      .from("channel_members")
      .delete()
      .eq("channel_id", id)
      .eq("user_id", user.id);
    setMembershipBusy(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    setComposerOpen(false);
    setNewPost("");
    setPhotoUrl(null);
    toast.success("Channel left");
    await fetchData();
  };

  const sharePayload = channel
    ? {
        version: 1 as const,
        type: "channel" as const,
        targetId: channel.id,
        title: `${channel.emoji} ${channel.name}`.trim(),
        subtitle: channel.description || "Community channel",
        imageUrl: null,
        route: `/community/channel/${channel.id}`,
        externalUrl: buildKindredWebUrl(`/community/channel/${channel.id}`),
        accessScope: "public" as const,
      }
    : null;

  if (!channel) {
    return (
      <MemberPageScaffold title="Channel" subtitle="Loading the channel and latest posts." fallbackPath="/communities?section=channels">
        <AppLoadingState title="Loading channel" body="Pulling in the latest posts and membership state." />
      </MemberPageScaffold>
    );
  }

  return (
    <MemberPageScaffold
      title={`${channel.emoji} ${channel.name}`.trim()}
      subtitle={channel.description || "Community channel"}
      fallbackPath="/communities?section=channels"
      titleLines={2}
      action={
        sharePayload ? (
          <KindredShareSheet
            payload={sharePayload}
            triggerContent={<Share2 size={16} />}
            iconOnly
          />
        ) : undefined
      }
    >
        <AppCard>
          <AppCardBody py="$sm">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {normalizeChannelCountryScope(channel.country_scope) === "ALL"
                    ? "Available to all members"
                    : getChannelCountryScopeLabel(channel.country_scope, "long")}
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {isJoined ? "You are in this channel" : "Join this channel"}
                </p>
                <p className="text-sm leading-6 text-muted-foreground">
                  {isJoined
                    ? "Leave if you want it to move back into Discover and become read-only for you."
                    : "You can browse posts first. Join when you want to post, reply, or react."}
                </p>
              </div>
              <AppButton
                tone={isJoined ? "outline" : "primary"}
                size="sm"
                minWidth={132}
                onPress={() => void (isJoined ? handleLeave() : handleJoin())}
                disabled={membershipBusy}
              >
                {membershipBusy ? "Saving..." : isJoined ? "Leave channel" : "Join channel"}
              </AppButton>
            </div>
          </AppCardBody>
        </AppCard>

        <div className="sticky top-3 z-10 -mx-1 rounded-[24px] bg-background/95 px-1 pb-2 pt-1 backdrop-blur-sm">
          <AppCard>
            <AppCardBody py="$sm">
              {isJoined && composerOpen ? (
                <div className="space-y-3">
                  <XStack alignItems="center" gap="$sm">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                        {(user?.user_metadata?.display_name ?? "Y")[0]}
                      </AvatarFallback>
                    </Avatar>
                    <AppText fontSize="$sm" lineHeight="$sm" fontWeight="600">Start a post</AppText>
                  </XStack>
                  <PhotoUploadButton
                    bucket="channel-photos"
                    onPhotoReady={setPhotoUrl}
                    onClear={() => setPhotoUrl(null)}
                    previewUrl={photoUrl}
                    returnObjectPath
                  />
                  <XStack alignItems="center" gap="$sm">
                    <MentionComposerField
                      value={newPost}
                      onChangeText={setNewPost}
                      mentions={newPostMentions}
                      onMentionsChange={setNewPostMentions}
                      lookupCandidates={(query) => searchChannelMentionSuggestions(id, query, user?.id)}
                      placeholder="Say something..."
                      fieldProps={{ flex: 1 }}
                      inputProps={{
                        flex: 1,
                        onKeyDown: (event: any) => {
                          if (event.key === "Enter") {
                            void sendPost();
                          }
                        },
                      }}
                    />
                    <AppButton
                      type="button"
                      width={44}
                      minWidth={44}
                      height={44}
                      px={0}
                      onPress={sendPost}
                      disabled={sending || (!newPost.trim() && !photoUrl)}
                      aria-label="Send post"
                    >
                      <Send size={16} />
                    </AppButton>
                  </XStack>
                  <div className="flex justify-end">
                    <AppButton
                      type="button"
                      tone="ghost"
                      size="sm"
                      onClick={() => {
                        setComposerOpen(false);
                        setNewPost("");
                        setNewPostMentions([]);
                        setPhotoUrl(null);
                      }}
                    >
                      Cancel
                    </AppButton>
                  </div>
                </div>
              ) : isJoined ? (
                <button type="button" onClick={() => setComposerOpen(true)} className="w-full text-left">
                  <XStack alignItems="center" gap="$sm">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                        {(user?.user_metadata?.display_name ?? "Y")[0]}
                      </AvatarFallback>
                    </Avatar>
                    <AppText fontSize="$sm" lineHeight="$sm" color="$colorMuted">Start a post</AppText>
                    <Pencil size={16} className="ml-auto text-primary" />
                  </XStack>
                </button>
              ) : (
                <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
                  Join this channel to start posting, reply in threads, and react.
                </AppText>
              )}
            </AppCardBody>
          </AppCard>
        </div>

        {posts.length === 0 && (
          <AppEmptyState title="No posts yet" body="Start the conversation when you're ready." />
        )}
        {posts.map((post) => {
          const rxns = getReactionsForPost(post.id);
          const photos = (post.photo_urls || [])
            .map((url: string) => resolveStoredObjectUrl("channel-photos", url, resolvedPhotoUrls))
            .filter((url: string | null): url is string => Boolean(url));
          const canEditPost = canEditOwnedContentOnly({
            viewerId: user?.id,
            ownerId: post.user_id,
          });
          const canRemovePost = canRemoveKinfeedOrChannelContent({
            viewerId: user?.id,
            ownerId: post.user_id,
            role,
          });
          const isAdminModeratingPost = isAdminModeratingOthersContent({
            viewerId: user?.id,
            ownerId: post.user_id,
            role,
          });
          const selectedReaction = Object.entries(rxns).find(([, value]) => value.myReaction)?.[0];

          return (
            <div
              key={post.id}
              className="space-y-1.5"
              {...longPressAction(() => setPostActionsId(post.id))}
            >
              <div className="flex gap-2.5">
                <Avatar className="h-10 w-10 shrink-0">
                  {post.profiles?.avatar_url && <AvatarImage src={post.profiles.avatar_url} alt={post.profiles?.display_name ?? "Member"} />}
                  <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                    {(post.profiles?.display_name ?? "K")[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-foreground">{post.profiles?.display_name ?? "Member"}</span>
                    <span className="text-xs text-muted-foreground">{formatTime(post.created_at)}</span>
                    <MessageActions
                      open={postActionsId === post.id}
                      onOpenChange={(open) => setPostActionsId(open ? post.id : null)}
                      selectedReaction={selectedReaction}
                      onReactionSelect={isJoined ? (emoji) => toggleReaction(post.id, emoji) : undefined}
                      actions={[
                        {
                          id: "reply",
                          label: "Reply",
                          icon: <Reply size={16} />,
                          onSelect: () => navigate(`/community/channel/${id}/post/${post.id}`),
                        },
                        ...(!canRemovePost
                          ? [
                              {
                                id: "report",
                                label: "Report",
                                icon: <Flag size={16} />,
                                onSelect: () => {
                                  setReportPostId(post.id);
                                  setReportOpen(true);
                                },
                              },
                            ]
                          : []),
                      ]}
                      onEdit={canEditPost ? () => {
                        const parsed = parseKindredShareContent(post.content);
                        setEditingId(post.id);
                        setEditText(parsed.payload ? parsed.text : post.content);
                      } : undefined}
                      onDelete={canRemovePost ? () => deletePost(post.id, isAdminModeratingPost) : undefined}
                      deleteLabel={isAdminModeratingPost ? "Remove post" : "Delete post"}
                      showEdit={canEditPost}
                    />
                  </div>
                  {editingId === post.id ? (
                    <div className="mt-1 space-y-2">
                      <AppInput value={editText} onChangeText={setEditText} />
                      <XStack gap="$sm">
                        <AppButton type="button" size="sm" onPress={() => saveEdit(post.id)}>Save</AppButton>
                        <AppButton type="button" tone="ghost" size="sm" onPress={() => setEditingId(null)}>Cancel</AppButton>
                      </XStack>
                    </div>
                  ) : (
                    <>
                      <KindredSharedContent content={post.content} textClassName="mt-0.5 text-sm leading-relaxed text-foreground" />
                      {post.edited_at && <span className="text-[10px] text-muted-foreground">Edited</span>}
                    </>
                  )}
                </div>
              </div>

              {/* Photos */}
              {photos.length > 0 && (
                <div className="ml-12 flex gap-2 overflow-x-auto">
                  {photos.map((url: string, i: number) => (
                    <button key={i} onClick={() => setFullScreenImage(url)} className="h-20 w-[120px] shrink-0 rounded-lg overflow-hidden">
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Reactions row */}
              <div className="ml-12 flex flex-wrap items-center gap-1.5">
                {Object.entries(rxns).map(([emoji, { count, myReaction }]) => (
                  <button
                    key={emoji}
                    onClick={() => toggleReaction(post.id, emoji)}
                    disabled={!isJoined}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors ${
                      myReaction ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    } ${!isJoined ? "cursor-not-allowed opacity-70" : ""}`}
                  >
                    {emoji} {count}
                  </button>
                ))}
              </div>

              <div className="ml-12">
                {(replyCounts[post.id] ?? 0) > 0 ? (
                  <button onClick={() => navigate(`/community/channel/${id}/post/${post.id}`)} className="text-xs font-medium text-primary hover:underline">
                    {replyCounts[post.id]} {replyCounts[post.id] === 1 ? "reply" : "replies"}
                  </button>
                ) : (
                  <button onClick={() => navigate(`/community/channel/${id}/post/${post.id}`)} className="text-xs text-muted-foreground hover:text-primary">
                    {isJoined ? "Reply" : "Open thread"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      
      {fullScreenImage && (
        <FullScreenImage images={[fullScreenImage]} currentIndex={0} onClose={() => setFullScreenImage(null)} onNavigate={() => {}} />
      )}
      <ReportSheet
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType="channel_post"
        contentId={reportPostId ?? undefined}
      />
    </MemberPageScaffold>
  );
};

export default ChannelView;
